/**
 * AdminApiaPanel — Admin space CRUD for Destine AP-IA.
 *
 * Three areas:
 *  1) Manual key generation (admin creates a key directly, no application needed)
 *  2) Applications (pending → approve / refuse)
 *  3) Issued keys (revoke)
 *
 * Text is translated by the app-wide admin i18n (see useAdminLang hook,
 * language switcher lives at the top of the Admin page — not inside this panel).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Key, Trash2, Copy, Loader2, ShieldCheck, XCircle, Info, Check, X,
  Inbox, Plus,
} from 'lucide-react';
import { useAdminLang } from '../hooks/useAdminLang';

const AdminApiaPanel = () => {
  const { at, adminLang } = useAdminLang();

  const [tab, setTab] = useState('pending'); // pending | approved | refused | keys
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, refused: 0 });
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopes, setScopes] = useState([]);
  const [showScopesInfo, setShowScopesInfo] = useState(false);
  const [manualScopes, setManualScopes] = useState(['text_ai']);
  const [busy, setBusy] = useState(null); // req id being processed
  const [refuseFor, setRefuseFor] = useState(null); // req id to refuse
  const [refuseReason, setRefuseReason] = useState('');
  const [newRawKey, setNewRawKey] = useState(null); // { name, raw_key }

  // Manual generation form
  const [manualName, setManualName] = useState('');
  const [manualDesc, setManualDesc] = useState('');
  const [manualBusy, setManualBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, keysRes, scopesRes] = await Promise.all([
        axios.get('/api/admin/apia/requests'),
        axios.get('/api/admin/apia/keys'),
        axios.get('/api/apia/scopes').catch(() => ({ data: { scopes: [] } })),
      ]);
      setRequests(reqRes.data?.requests || []);
      setStats(reqRes.data?.stats || { pending: 0, approved: 0, refused: 0 });
      setKeys(keysRes.data?.keys || []);
      setScopes(scopesRes.data?.scopes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (req) => {
    setBusy(req.id);
    try {
      const r = await axios.post(`/api/admin/apia/requests/${req.id}/approve`, { admin_note: '' });
      setNewRawKey({ name: r.data?.key?.name, raw_key: r.data?.key?.raw_key });
      toast.success(at('apiaApproved'));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(null);
    }
  };

  const openRefuse = (req) => { setRefuseFor(req); setRefuseReason(''); };

  const submitRefuse = async () => {
    if (refuseReason.trim().length < 5) return;
    setBusy(refuseFor.id);
    try {
      await axios.post(`/api/admin/apia/requests/${refuseFor.id}/refuse`, { reason: refuseReason.trim() });
      toast.success(at('apiaRefused'));
      setRefuseFor(null);
      setRefuseReason('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (key) => {
    if (!window.confirm(at('confirmRevoke'))) return;
    try {
      await axios.delete(`/api/admin/apia/keys/${key.id}`);
      toast.success(at('apiaKeyRevoked'));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const copyRaw = async () => {
    try { await navigator.clipboard.writeText(newRawKey.raw_key); toast.success(at('copiedKey')); }
    catch { toast.error('Copy failed'); }
  };

  const createManualKey = async () => {
    const n = manualName.trim();
    if (!n) return;
    setManualBusy(true);
    try {
      const r = await axios.post('/api/admin/apia/keys', { name: n, description: manualDesc.trim(), scopes: manualScopes });
      setNewRawKey({ name: r.data.name, raw_key: r.data.raw_key });
      setManualName('');
      setManualDesc('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setManualBusy(false);
    }
  };

  const filtered = tab === 'keys'
    ? []
    : requests.filter(r => r.status === tab);

  const active = keys.filter(k => !k.revoked_at);
  const revoked = keys.filter(k => k.revoked_at);

  return (
    <div className="space-y-4" data-testid="admin-apia-panel">
      {/* Header with info tooltip on capabilities */}
      <div className="rounded-2xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-400/30 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-indigo-300 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-indigo-100 flex-1 min-w-0">
            <div className="font-semibold text-white mb-1 flex items-center gap-2">
              <span>{at('tabApia')}</span>
              <button
                type="button"
                onClick={() => setShowScopesInfo(v => !v)}
                className="p-1 rounded-full hover:bg-white/10 text-indigo-200"
                title="Voir les capacités"
                data-testid="apia-scopes-info-btn"
              >
                <Info className="w-4 h-4" />
              </button>
            </div>
            <p className="text-white/80 text-xs">
              Header <code className="px-1.5 py-0.5 rounded bg-black/30 text-indigo-200 font-mono">X-APIA-Key</code> · endpoints{' '}
              <code className="px-1.5 py-0.5 rounded bg-black/30 text-indigo-200 font-mono">POST /api/apia/chat</code>
              {' · '}<code className="px-1.5 py-0.5 rounded bg-black/30 text-indigo-200 font-mono">GET /api/apia/me</code>
            </p>
            {showScopesInfo && scopes.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="apia-scopes-info-panel">
                <p className="text-[11px] text-indigo-200/80">Chaque demande PRO cible une ou plusieurs capacités. Le quota gratuit (60/min · 1 000/jour) et les packs de recharge sont identiques quelle que soit la capacité.</p>
                <ul className="space-y-1.5">
                  {scopes.map(sc => {
                    const label = sc.labels?.[adminLang] || sc.labels?.fr || sc.id;
                    const desc = sc.descriptions?.[adminLang] || sc.descriptions?.fr || '';
                    return (
                      <li key={sc.id} className="rounded-lg bg-black/25 border border-white/10 p-2" data-testid={`apia-scope-info-${sc.id}`}>
                        <div className="text-xs font-semibold text-white flex items-center gap-1.5 flex-wrap">
                          <span>{label}</span>
                          {sc.available ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/25 text-emerald-200">Actif</span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/25 text-amber-200">Bientôt</span>
                          )}
                        </div>
                        <p className="text-[10.5px] text-white/70 mt-0.5 leading-snug">{desc}</p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manual key generation (admins can create keys directly, without a request) */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
        <h4 className="font-bold mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4 text-fuchsia-300" /> {at('apiaManualTitle')}
        </h4>
        <p className="text-xs text-white/60 mb-3">{at('apiaManualHint')}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input
            value={manualName}
            onChange={e => setManualName(e.target.value)}
            placeholder={at('apiaManualNamePh')}
            className="md:col-span-2 bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-400"
            data-testid="apia-manual-name"
          />
          <button
            onClick={createManualKey}
            disabled={manualBusy || !manualName.trim()}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-1.5"
            data-testid="apia-manual-create-btn"
          >
            {manualBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
            {at('apiaManualGenerate')}
          </button>
        </div>
        <textarea
          value={manualDesc}
          onChange={e => setManualDesc(e.target.value)}
          placeholder={at('apiaManualDescPh')}
          rows={2}
          className="mt-2 w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-400"
          data-testid="apia-manual-desc"
        />
        {scopes.length > 0 && (
          <div className="mt-2" data-testid="apia-manual-scopes">
            <p className="text-[11px] text-white/60 mb-1.5">Capacités accordées</p>
            <div className="flex flex-wrap gap-1.5">
              {scopes.map(sc => {
                const label = sc.labels?.[adminLang] || sc.labels?.fr || sc.id;
                const checked = manualScopes.includes(sc.id);
                return (
                  <button
                    key={sc.id}
                    type="button"
                    onClick={() => setManualScopes(prev => prev.includes(sc.id) ? prev.filter(s => s !== sc.id) : [...prev, sc.id])}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${checked ? 'bg-fuchsia-500/25 border-fuchsia-400 text-fuchsia-100' : 'bg-white/5 border-white/15 text-white/70 hover:bg-white/10'}`}
                    data-testid={`apia-manual-scope-${sc.id}`}
                  >
                    {label}{!sc.available && ' · bientôt'}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2">
        {[
          { k: 'pending', label: `${at('apiaTabPending')} (${stats.pending})` },
          { k: 'approved', label: `${at('apiaTabApproved')} (${stats.approved})` },
          { k: 'refused', label: `${at('apiaTabRefused')} (${stats.refused})` },
          { k: 'keys', label: `${at('apiaKeysTitle')} (${active.length}/${keys.length})` },
        ].map(x => (
          <button
            key={x.k}
            onClick={() => setTab(x.k)}
            data-testid={`admin-apia-subtab-${x.k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              tab === x.k ? 'bg-fuchsia-500 border-fuchsia-400 text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
            }`}
          >
            {x.label}
          </button>
        ))}
      </div>

      {/* Newly created raw key banner (after approve) */}
      {newRawKey && (
        <div className="rounded-2xl bg-emerald-500/10 border-2 border-emerald-400/50 p-4" data-testid="apia-new-key-banner">
          <div className="flex items-center justify-between mb-2">
            <div className="font-bold text-emerald-300">
              {at('keyOnceBanner', { name: newRawKey.name || '' })}
            </div>
            <button onClick={() => setNewRawKey(null)} className="text-emerald-300/70 hover:text-emerald-100"><XCircle className="w-5 h-5" /></button>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-black/40 font-mono text-sm text-emerald-200 select-all break-all" data-testid="apia-new-key-value">
            {newRawKey.raw_key}
            <button onClick={copyRaw} className="p-1 rounded hover:bg-white/10 flex-shrink-0" title={at('copy')}><Copy className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-amber-200 mt-2 flex items-start gap-1"><Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {at('copiedKey')}</p>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : tab === 'keys' ? (
        <KeysList keys={keys} at={at} onRevoke={revoke} />
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-white/40 border border-dashed border-white/10 rounded-xl">
          <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">{at('apiaRequestsEmpty')}</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="apia-requests-list">
          {filtered.map(r => (
            <li key={r.id} className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid={`apia-request-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white">{r.project_name}</span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-white/60">{r.user_name || r.user_email}</span>
                  </div>
                  <div className="text-[11px] text-white/40 mt-0.5">{at('apiaSubmittedOn')}: {r.created_at?.slice(0, 16).replace('T', ' ')}</div>
                  <div className="text-xs text-white/80 mt-2 whitespace-pre-wrap break-words"><strong>{at('apiaPurpose')}:</strong> {r.purpose}</div>
                  <div className="text-xs text-white/70 mt-1 whitespace-pre-wrap break-words"><strong>{at('apiaReason')}:</strong> {r.reason}</div>
                  {r.status === 'refused' && (
                    <div className="text-xs text-red-300 mt-2"><strong>{at('apiaRefuse')}:</strong> {r.refuse_reason}</div>
                  )}
                  {r.status === 'approved' && r.key_id && (
                    <div className="text-xs text-emerald-300 mt-2 font-mono">
                      {keys.find(k => k.id === r.key_id)?.key_prefix || r.key_id}
                    </div>
                  )}
                </div>
                {r.status === 'pending' && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      data-testid={`apia-approve-${r.id}`}
                      disabled={busy === r.id}
                      onClick={() => approve(r)}
                      className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-50"
                    >
                      {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      {at('apiaApprove')}
                    </button>
                    <button
                      data-testid={`apia-refuse-${r.id}`}
                      onClick={() => openRefuse(r)}
                      className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/40 border border-red-400/40 text-red-200 text-xs font-semibold flex items-center gap-1"
                    >
                      <X className="w-3.5 h-3.5" />
                      {at('apiaRefuse')}
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Refuse dialog */}
      {refuseFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setRefuseFor(null)}>
          <div className="w-full max-w-md rounded-2xl bg-neutral-900 border border-white/10 p-4" onClick={(e) => e.stopPropagation()} data-testid="apia-refuse-dialog">
            <h4 className="font-bold mb-2">{at('apiaRefuse')} — {refuseFor.project_name}</h4>
            <textarea
              data-testid="apia-refuse-reason"
              value={refuseReason}
              onChange={e => setRefuseReason(e.target.value)}
              placeholder={at('apiaRefusePrompt')}
              rows={4}
              className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setRefuseFor(null)} className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm">{at('cancel')}</button>
              <button
                data-testid="apia-refuse-confirm"
                onClick={submitRefuse}
                disabled={refuseReason.trim().length < 5 || busy === refuseFor.id}
                className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-semibold"
              >
                {busy === refuseFor.id ? <Loader2 className="w-4 h-4 animate-spin" /> : at('apiaRefuse')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const KeysList = ({ keys, at, onRevoke }) => {
  if (keys.length === 0) {
    return (
      <div className="text-center py-8 text-white/40 border border-dashed border-white/10 rounded-xl">
        <Key className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">—</p>
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="apia-keys-list">
      {keys.map(k => (
        <li key={k.id} className={`rounded-xl bg-white/5 border p-3 ${k.revoked_at ? 'border-red-500/30 opacity-60' : 'border-white/10'}`} data-testid={`apia-key-${k.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-white">{k.name}</span>
                {k.revoked_at ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">{at('apiaKeyRevoked')}</span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">●</span>
                )}
                <span className="font-mono text-xs text-fuchsia-300">{k.key_prefix}</span>
              </div>
              {k.description && <div className="text-xs text-white/60 mt-0.5 truncate">{k.description}</div>}
              <div className="text-[10px] text-white/40 mt-1 flex items-center gap-3 flex-wrap">
                <span>{at('apiaSubmittedOn')}: {k.created_at?.slice(0, 10)}</span>
                <span>· {(k.usage_count || 0)} req</span>
                {k.last_used_at && <span>· {k.last_used_at.slice(0, 16).replace('T', ' ')}</span>}
              </div>
            </div>
            {!k.revoked_at && (
              <button
                onClick={() => onRevoke(k)}
                className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-300 flex-shrink-0"
                title={at('apiaKeyRevoke')}
                data-testid={`apia-revoke-${k.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

export default AdminApiaPanel;
